import { gql, useQuery, useMutation } from '@apollo/client';
import { NetworkStatus } from '@apollo/client';
import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { membership_plan_viewed } from '@/analytics';
import { BigRadioGroup } from '@/components/BigRadio';
import { CorporateEnrollmentFlow } from '@/components/CorporateEnrollmentFlow';
import { RequestAccessFormModal } from '@/components/CorporateEnrollmentFlow/modals/RequestAccessFormModal';
import { RequestAccessListModal } from '@/components/CorporateEnrollmentFlow/modals/RequestAccessListModal';
import { RequestSubmittedModal } from '@/components/CorporateEnrollmentFlow/modals/RequestSubmittedModal';
import { RequestFailedModal } from '@/components/CorporateEnrollmentFlow/modals/RequestFailedModal';
import { ReviewRequestModal } from '@/components/CorporateEnrollmentFlow/modals/ReviewRequestModal';
import { LogInSignUpModal } from '@/components/LogInSignUp';
import { Sentinel } from '@/components/Sentinel';
import { EUserRole } from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { useElementHeight } from '@/hooks/useElementHeight';
import { usePermissions } from '@/hooks/usePermissions';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Emoji } from '@/imports/ui/chakra/components/Emoji';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { Butterfly, ButterflyMain } from '@/layouts/Butterfly';

import {
  MembershipsPage_MembershipTypes,
  MembershipsPage_MembershipTypesVariables,
} from './graphql-types/MembershipsPage_MembershipTypes';
import { MembershipsPage_Query } from './graphql-types/MembershipsPage_Query';

import { MembershipsPageSubmitFooter } from './components/MembershipsPageSubmitFooter/MembershipsPageSubmitFooter';
import { RadioCard } from './components/RadioCard';
import { RadioCard_MembershipTypeFragment } from './components/RadioCard/graphql-types/RadioCard_MembershipTypeFragment';

const GET_ALL_MEMBERSHIPS = gql`
  query MembershipsPage_MembershipTypes($input: MembershipTypesInput!) {
    membershipTypes(input: $input) {
      ... on MembershipTypesSuccess {
        results {
          _id
          ...RadioCard_MembershipTypeFragment
          __typename
        }
        totalCount
        message
        __typename
      }
      __typename
    }
  }
  ${RadioCard.membershipTypeFragment}
`;

const QUERY = gql`
  query MembershipsPage_Query {
    tenant {
      result {
        _id
        customExploreMembershipsLinkV2 {
          url
        }
      }
    }
    currentUser {
      _id
      membershipData {
        currentMembershipTerm {
          _id
          membershipType {
            _id
            name
          }
        }
      }
      rosterOrganization {
        _id
        isCurrentUserInheritingBenefits
        membershipData {
          currentMembershipTerm {
            _id
            membershipType {
              _id
              name
            }
          }
        }
      }
    }
  }
`;

const GET_COMPANY_ENROLLMENT_ORGANIZATIONS = gql`
  query OrganizationsWithActiveCompanyPaidEmployeeEnrollmentMembership($input: OrganizationsWithActiveCompanyPaidEmployeeEnrollmentMembershipInput) {
    organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership(input: $input) {
      totalCount
      results {
        organizationId
        organizationName
        numberOfSeats
        activeMembershipTermId
        membershipTypeId
        membershipTypeName
        corporateMembershipModel
        permissions {
          organizationCanAddEmployees
          employeeCanRequestOnline
        }
      }
    }
  }
`;

const REQUEST_ORGANIZATION_MEMBERSHIP = gql`
  mutation RequestOrganizationMembership($input: RequestOrganizationMembershipInput!) {
    requestOrganizationMembership(input: $input) {
      __typename
      ... on RequestOrganizationMembershipSuccess {
        message
        request {
          _id
          organizationId
          userId
          membershipTypeId
          requestReason
          status
          requestedAt
          reviewedBy
          reviewedAt
          rejectionReason
        }
      }
      ... on RequestOrganizationMembershipDuplicateRequestError {
        message
      }
      ... on RequestOrganizationMembershipAlreadyActiveMemberError {
        message
      }
      ... on RequestOrganizationMembershipAlreadyPartOfAnotherOrganizationError {
        message
      }
    }
  }
`;

const LIMIT = 2000;
const ORG_LIMIT = 10;

interface CorporateOrganization {
  _id: string;
  name: string;
  membershipType: string;
  membershipTypeId: string;
  numberOfSeats?: number | null;
}

interface CorporateRequestReviewData {
  organizationId: string;
  membershipTypeId: string;
  organization: string;
  membership: string;
  firstName: string;
  lastName: string;
  workEmail: string;
  jobTitle: string;
  department: string;
  phoneNumber: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  approvalMethod: 'organization';
}

export const MembershipsPage: React.FC = () => {
  const router = useRouter();
  const current = useCurrent();
  const associationName =
    current.tenant?.displayName || current.tenant?.acronym || '';
  const footer = useElementHeight();
  const [offset, setOffset] = React.useState(0);
  const [orgOffset, setOrgOffset] = React.useState(0);

  const query = useQuery<MembershipsPage_Query>(QUERY);

  const isCurrentUserInheritingBenefits =
    !!query.data?.currentUser?.rosterOrganization
      ?.isCurrentUserInheritingBenefits;

  const {
    data: membershipTypesQuery,
    loading: membershipTypesLoading,
    fetchMore,
    networkStatus,
  } = useQuery<
    MembershipsPage_MembershipTypes,
    MembershipsPage_MembershipTypesVariables
  >(GET_ALL_MEMBERSHIPS, {
    variables: {
      input: {
        limit: LIMIT,
        offset: 0,
        filter: {
          isPublic: true,
          isActive: true,
          includeCorporateMemberships: true,
          includeIndividualMemberships: isCurrentUserInheritingBenefits
            ? false
            : true,
        },
        userId: current.user?._id,
      },
    },
    skip: query.loading,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const hasMore =
    (membershipTypesQuery?.membershipTypes?.totalCount ?? 0) >
    LIMIT * (offset + 1);

  const onLoadMore = React.useCallback(() => {
    if (hasMore) {
      const newOffset = offset + 1;
      setOffset(newOffset);
      fetchMore({
        variables: {
          input: {
            limit: LIMIT,
            offset: LIMIT * newOffset,
            filter: {
              isPublic: true,
              isActive: true,
              includeCorporateMemberships: true,
              includeIndividualMemberships: isCurrentUserInheritingBenefits
                ? false
                : true,
            },
            userId: current.user?._id,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...prev,
            membershipTypes: {
              ...prev.membershipTypes,
              ...fetchMoreResult.membershipTypes,
              results: [
                ...(prev.membershipTypes?.results || []),
                ...(fetchMoreResult.membershipTypes?.results || []),
              ],
            },
          };
        },
      });
    }
  }, [
    current.user?._id,
    fetchMore,
    hasMore,
    isCurrentUserInheritingBenefits,
    offset,
  ]);

  const permissions = usePermissions();
  const loginDisclosure = useDisclosure();

  const [value, setValue] = React.useState<string | undefined>(undefined);
  const [sortedMembershipTypes, setSortedMembershipTypes] = React.useState<
    RadioCard_MembershipTypeFragment[]
  >([]);

  const [isCorporateFlowOpen, setIsCorporateFlowOpen] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('enrollmentFlowStep');
      return !!(stored && stored !== 'CLOSED');
    }
    return false;
  });

  const [requestMembership, { loading: isSubmittingRequest }] = useMutation<any>(
    REQUEST_ORGANIZATION_MEMBERSHIP
  );

  const { data: orgData, loading: orgLoading, fetchMore: orgFetchMore, networkStatus: orgNetworkStatus } = useQuery<any>(
    GET_COMPANY_ENROLLMENT_ORGANIZATIONS,
    {
      variables: {
        input: {
          limit: ORG_LIMIT,
          offset: 0,
        },
      },
      skip: !isCorporateFlowOpen,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }
  );

  const orgLoadingMore = orgNetworkStatus === NetworkStatus.fetchMore;
  const orgTotalCount = orgData?.organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership?.totalCount ?? 0;
  const orgHasMore = orgTotalCount > ORG_LIMIT * (orgOffset + 1);

  const onOrgLoadMore = React.useCallback(() => {
    if (orgHasMore && !orgLoadingMore) {
      const newOffset = orgOffset + 1;
      setOrgOffset(newOffset);
      orgFetchMore({
        variables: {
          input: {
            limit: ORG_LIMIT,
            offset: ORG_LIMIT * newOffset,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...prev,
            organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership: {
              ...prev.organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership,
              ...fetchMoreResult.organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership,
              results: [
                ...(prev.organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership?.results || []),
                ...(fetchMoreResult.organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership?.results || []),
              ],
            },
          };
        },
      });
    }
  }, [orgFetchMore, orgHasMore, orgLoadingMore, orgOffset]);

  const corporateOrganizations: CorporateOrganization[] = React.useMemo(() => {
    const results =
      orgData?.organizationsWithActiveCompanyPaidEmployeeEnrollmentMembership
        ?.results || [];
    return results.map((org: any) => ({
      _id: org.organizationId,
      name: org.organizationName,
      membershipType: org.membershipTypeName,
      membershipTypeId: org.membershipTypeId,
      numberOfSeats: org.numberOfSeats,
    }));
  }, [orgData]);

  const [selectedOrganization, setSelectedOrganization] = React.useState<
    CorporateOrganization | null
  >(null);
  const [requestReviewData, setRequestReviewData] = React.useState<
    CorporateRequestReviewData | null
  >(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('enrollmentRequestReviewData');
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  });
  const [requestError, setRequestError] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (query.data?.tenant.result?.customExploreMembershipsLinkV2?.url) {
      window.location.replace(
        query.data.tenant.result.customExploreMembershipsLinkV2.url
      );
    }
  }, [query.data?.tenant.result?.customExploreMembershipsLinkV2?.url]);

  // "Add Future Membership" sends the user here to pick next year's membership
  // for themselves - they are not looking for an employer to enrol under, so
  // the corporate enrollment intro stays closed for that entry point.
  const isAddingFutureMembership =
    router.query.isFutureMembership === 'true';

  // An Account Owner who already inherits their organization's membership has
  // nothing to enrol in, so the corporate enrollment intro is never offered to
  // them - and `query` has to have resolved before we can tell, otherwise the
  // modal flashes open for them on first render. `router.isReady` gates the
  // same way for the query param above. Guests can browse the list, but
  // requesting access needs a signed-in user, so the intro waits for login.
  const shouldOfferCorporateEnrollment =
    permissions.isAuthenticated &&
    !query.loading &&
    router.isReady &&
    !isAddingFutureMembership &&
    !(
      current.user?.role === EUserRole.ORGANIZATION_ACCOUNT_OWNER &&
      isCurrentUserInheritingBenefits
    );

  React.useEffect(() => {
    if (router.isReady && shouldOfferCorporateEnrollment) {
      setIsCorporateFlowOpen(true);
    }
  }, [router.isReady, shouldOfferCorporateEnrollment]);

  const selectedMembershipType = sortedMembershipTypes.find(
    (membershipType) => membershipType._id === value
  );

  const resetCorporateRequest = React.useCallback(() => {
    setSelectedOrganization(null);
    setRequestReviewData(null);
    setRequestError(undefined);
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('enrollmentRequestReviewData');
    }
  }, []);

  React.useEffect(() => {
    if (requestReviewData) {
      sessionStorage.setItem('enrollmentRequestReviewData', JSON.stringify(requestReviewData));
    } else {
      sessionStorage.removeItem('enrollmentRequestReviewData');
    }
  }, [requestReviewData]);

  React.useEffect(() => {
    const sortedMembershipTypes =[...membershipTypesQuery?.membershipTypes?.results || []].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    setSortedMembershipTypes(sortedMembershipTypes);
  }, [membershipTypesQuery?.membershipTypes?.results]);

  React.useEffect(() => {
    if (!sortedMembershipTypes.length) return;

    membership_plan_viewed({
      entity_id: 'memberships',
      entity_type: 'membership_plan',
      cart_item_count: sortedMembershipTypes.length,
      page_type: 'membership',
      page_section: 'membership',
    });
  }, [sortedMembershipTypes.length]);

  const emptyMembershipMessageBase =
    membershipTypesQuery?.membershipTypes?.message?.trim();
  const emptyMembershipMessage = emptyMembershipMessageBase
    ? `${emptyMembershipMessageBase} Redirecting back to membership page`
    : null;
  const hasNoMembershipTypes =
    !membershipTypesLoading && !loadingMore && sortedMembershipTypes.length === 0;
  const shouldShowEmptyMembershipMessage =
    hasNoMembershipTypes && !!emptyMembershipMessage;

  React.useEffect(() => {
    if (!shouldShowEmptyMembershipMessage || !permissions.isAuthenticated) return;
    if (router.asPath.startsWith('/me/membership')) return;

    const timer = window.setTimeout(() => {
      router.replace('/me/membership');
    }, 2200);

    return () => window.clearTimeout(timer);
  }, [permissions.isAuthenticated, router, shouldShowEmptyMembershipMessage]);

  return (
    <Butterfly>
      {query.loading ||
      query.data?.tenant.result?.customExploreMembershipsLinkV2?.url ? (
        <Loading />
      ) : query.error ? (
        <UnexpectedError />
      ) : (
        <ButterflyMain>
          <Stack
            align="center"
            spacing="8"
            maxW="container.md"
            mx="auto"
            p="4"
            mb={`${footer.height}px`}
          >
            <Stack spacing="1" align="center" mt="4">
              <Heading size="md">
                {permissions.isAuthenticated
                  ? 'Explore Memberships'
                  : 'Become a Member'}
              </Heading>
              <Text color="gray.600" mb="8">
                Explore {associationName}’s range of memberships and member
                benefits
              </Text>
            </Stack>

            {membershipTypesLoading && !loadingMore ? (
              <Loading />
            ) : (
              <>
                {shouldShowEmptyMembershipMessage ? (
                  <Box
                    w="full"
                    bg="green.50"
                    border="1px solid"
                    borderColor="green.500"
                    borderRadius="md"
                    p="4"
                  >
                    <Text color="green.500" textStyle="xl">
                      {emptyMembershipMessage}
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing="4" w="full">
                    <BigRadioGroup
                      value={value}
                      onChange={(value) => setValue(value)}
                      variant="spaced"
                      w="full"
                    >
                      {sortedMembershipTypes.map(
                        (membershipType) => (
                          <RadioCard
                            key={membershipType._id}
                            membershipType={membershipType}
                            value={membershipType._id}
                            isChecked={value === membershipType._id}
                          />
                        )
                      )}
                    </BigRadioGroup>
                  </VStack>
                )}

                {hasMore && (
                  <Box w="full">
                    {loadingMore ? (
                      <Loading />
                    ) : (
                      <Sentinel
                        onIntersection={onLoadMore}
                        rootMargin="500px 0px"
                      />
                    )}
                  </Box>
                )}

                {!permissions.isAuthenticated && (
                  <>
                    <Card w="full" p="6" mt="8">
                      <Stack alignItems="center" spacing="4">
                        <Emoji symbol="☝️" fontSize="3xl" />

                        <Stack spacing="0" align="center">
                          <Text textStyle="bodyStrong">
                            Trying to renew an existing membership?
                          </Text>

                          <Text color="gray.600">
                            Sign in to your {associationName} account to access
                            your membership portal.
                          </Text>
                        </Stack>

                        <Button
                          variant="outline"
                          colorScheme="gray"
                          w="fit-content"
                          onClick={loginDisclosure.onOpen}
                        >
                          Sign in
                        </Button>
                      </Stack>
                    </Card>

                    <LogInSignUpModal
                      isOpen={loginDisclosure.isOpen}
                      onClose={loginDisclosure.onClose}
                    />
                  </>
                )}
              </>
            )}
          </Stack>
          <Box
            ref={footer.ref}
            position="fixed"
            bottom="0"
            left="0"
            right="0"
            borderTopWidth="1px"
            borderTopColor="gray.100"
          >
            <MembershipsPageSubmitFooter
              selectedMembershipTypeId={value}
              isCurrentUserMembership={
                query.data?.currentUser?.membershipData?.currentMembershipTerm
                  ?.membershipType._id === value ||
                query.data?.currentUser?.rosterOrganization?.membershipData
                  ?.currentMembershipTerm?.membershipType._id === value
              }
            />
          </Box>

          {/* Corporate Enrollment Flow - Open on request access click */}
          <CorporateEnrollmentFlow
            openOnMount={isCorporateFlowOpen && shouldOfferCorporateEnrollment}
            onClose={() => setIsCorporateFlowOpen(false)}
            onDeclineCorporateOption={() => {
              setIsCorporateFlowOpen(false);
            }}
            onCreateNewOrganization={() => {
              router.push('/my-company/setup');
            }}
            renderRequestAccessList={({ onBack, onSelectOrganization, onClose }) => {
              const currentUser = query.data?.currentUser;
              const rosterOrg = currentUser?.rosterOrganization;
              const showAlreadyMemberNote = !!(
                rosterOrg &&
                !rosterOrg.isCurrentUserInheritingBenefits
              );

              return (
              <RequestAccessListModal
                isOpen
                showAlreadyMemberNote={showAlreadyMemberNote}
                onBack={onBack}
                onClose={() => {
                  resetCorporateRequest();
                  onClose();
                }}
                organizations={corporateOrganizations}
                isLoading={orgLoading && !orgLoadingMore}
                hasMore={orgHasMore}
                loadingMore={orgLoadingMore}
                onLoadMore={onOrgLoadMore}
                onSelectOrganization={(organizationId) => {
                  const org = corporateOrganizations.find(
                    (item) => item._id === organizationId
                  );
                  setSelectedOrganization(org ?? null);
                  onSelectOrganization(organizationId);
                }}
              />
              );
            }}
            renderRequestAccessForm={({
              organizationId,
              onBack,
              onSubmit,
              onClose,
            }) => (
              <RequestAccessFormModal
                isOpen
                organizationId={organizationId}
                organizationName={selectedOrganization?.name || corporateOrganizations.find(o => o._id === organizationId)?.name}
                membershipType={selectedOrganization?.membershipType || corporateOrganizations.find(o => o._id === organizationId)?.membershipType}
                userProfile={{
                  firstName: current.user?.firstName || '',
                  lastName: current.user?.lastName || '',
                  workEmail: current.user?.email || '',
                  jobTitle: (current.user as any)?.jobTitle || '',
                  department: (current.user as any)?.department || '',
                  phoneNumber: (current.user as any)?.primaryPhone || '',
                }}
                initialValues={
                  requestReviewData || {
                    firstName: current.user?.firstName || '',
                    lastName: current.user?.lastName || '',
                    workEmail: current.user?.email || '',
                    jobTitle: (current.user as any)?.jobTitle || '',
                    department: (current.user as any)?.department || '',
                    phoneNumber: (current.user as any)?.primaryPhone || '',
                  }
                }
                onBack={() => {
                  resetCorporateRequest();
                  onBack();
                }}
                onClose={() => {
                  resetCorporateRequest();
                  onClose();
                }}
                onSubmit={(data) => {
                  const activeOrg = selectedOrganization || corporateOrganizations.find(o => o._id === organizationId);
                  if (activeOrg) {
                    // Always organization-level now: association approval is
                    // no longer a separate mode, it is baseline access that
                    // every association admin has.
                    const approvalMethod = 'organization' as const;

                    setRequestReviewData({
                      organizationId: activeOrg._id,
                      membershipTypeId: activeOrg.membershipTypeId,
                      organization: activeOrg.name,
                      membership: activeOrg.membershipType,
                      firstName: data.firstName,
                      lastName: data.lastName,
                      workEmail: data.workEmail,
                      jobTitle: data.jobTitle,
                      department: data.department,
                      phoneNumber: data.phoneNumber,
                      reason: data.reason,
                      status: 'pending',
                      approvalMethod,
                    });
                  }
                  onSubmit(data);
                }}
              />
            )}
            renderRequestSubmitted={({ onClose, onViewStatus }) => (
              <RequestSubmittedModal
                isOpen
                onClose={() => {
                  resetCorporateRequest();
                  onClose();
                }}
                onViewStatus={onViewStatus}
                organizationName={selectedOrganization?.name}
                membershipType={selectedOrganization?.membershipType}
                userEmail={requestReviewData?.workEmail}
              />
            )}
            renderRequestFailed={({ onClose, onRestart }) => (
              <RequestFailedModal
                isOpen
                onClose={() => {
                  resetCorporateRequest();
                  sessionStorage.removeItem('enrollmentFlowStep');
                  sessionStorage.removeItem('enrollmentOrganizationId');
                  if (onRestart) {
                    onRestart();
                  } else {
                    onClose();
                  }
                }}
                errorMessage={requestError}
              />
            )}
            renderReviewRequest={({ onBack, onConfirm, onError, onClose }) => (
              <ReviewRequestModal
                isOpen
                isSubmitting={isSubmittingRequest}
                requestDetails={requestReviewData ?? undefined}
                onBack={onBack}
                onClose={() => {
                  resetCorporateRequest();
                  onClose();
                }}
                onConfirm={async () => {
                  if (!requestReviewData) return;
                  try {
                    const res = await requestMembership({
                      variables: {
                        input: {
                          organizationId: requestReviewData.organizationId,
                          membershipTypeId: requestReviewData.membershipTypeId,
                          requestReason: requestReviewData.reason,
                          jobTitle: requestReviewData.jobTitle,
                          department: requestReviewData.department,
                          primaryPhone: requestReviewData.phoneNumber, 
                        },
                      },
                    });
                    const data = res.data?.requestOrganizationMembership;
                    if (data?.__typename === 'RequestOrganizationMembershipSuccess') {
                      if (onConfirm) onConfirm();
                    } else if (data?.__typename === 'RequestOrganizationMembershipDuplicateRequestError') {
                      setRequestError(data.message || 'You already have a pending request for this organization.');
                      if (onError) onError();
                    } else if (data?.__typename === 'RequestOrganizationMembershipAlreadyActiveMemberError') {
                      setRequestError(data.message || 'You already have an active membership and cannot submit a new membership request.');
                      if (onError) onError();
                    } else if (data?.__typename === 'RequestOrganizationMembershipAlreadyPartOfAnotherOrganizationError') {
                      setRequestError(data.message || 'You are already part of another organization and cannot submit a request to join this one.');
                      if (onError) onError();
                    }
                  } catch (err: any) {
                    setRequestError(err.message || 'An error occurred.');
                    if (onError) onError();
                  }
                }}
              />
            )}
          />
        </ButterflyMain>
      )}
    </Butterfly>
  );
};
